import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import { queryCustomerInfo, PavoPayCustomerParty, PavoPayCustomerQueryResult } from '../../hooks/fiscalDevices/pavopay';
import { accountingApi } from '../../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../../config/api';
import { queryCustomerInfoViaRestP2P } from '../../../../../services/restP2PCustomerQueryService';
import { useTranslation } from 'react-i18next';

type InvoiceType = 'e-fatura' | 'e-arsiv' | null;
type ModalStep = 'select-type' | 'query' | 'result';

interface CustomerQueryModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onSelectForSale: (customerParty: PavoPayCustomerParty, isEInvoice: boolean, displayName: string, notificationPhone?: string) => void;
  onSaveAndSelect: (customerParty: PavoPayCustomerParty, isEInvoice: boolean, savedAccountId: string, displayName: string, notificationPhone?: string) => void;
  /**
   * Rest-P2P aktif ise TÜRMOB sorgusu local PavoPay servisine değil,
   * gts_pavo cihazına SignalR üzerinden yönlendirilir.
   */
  isRestP2PEnabled?: boolean;
}

const CustomerQueryModal: React.FC<CustomerQueryModalProps> = ({
  open,
  onClose,
  companyId,
  onSelectForSale,
  onSaveAndSelect,
  isRestP2PEnabled = false,
}) => {
  const { t } = useTranslation('salesSystem');
  const [step, setStep] = useState<ModalStep>('select-type');
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType>(null);
  const [taxInput, setTaxInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [queryResult, setQueryResult] = useState<PavoPayCustomerQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [notificationPhone, setNotificationPhone] = useState('');

  const isVkn = taxInput.length === 10;
  const isTckn = taxInput.length === 11;
  const isValidInput = isVkn || isTckn;
  const isEInvoice = selectedInvoiceType === 'e-fatura';

  const handleSelectType = useCallback((type: InvoiceType) => {
    setSelectedInvoiceType(type);
    setStep('query');
    setTaxInput('');
    setQueryResult(null);
    setError(null);
    setSaveSuccess(false);
    setNotificationPhone('');
  }, []);

  const handleBack = useCallback(() => {
    if (step === 'query') {
      setStep('select-type');
      setSelectedInvoiceType(null);
      setTaxInput('');
      setQueryResult(null);
      setError(null);
    }
  }, [step]);

  const handleQuery = useCallback(async () => {
    if (!isValidInput) return;

    setLoading(true);
    setError(null);
    setQueryResult(null);
    setSaveSuccess(false);

    try {
      const tcknArg = isTckn ? taxInput : undefined;
      const vknArg = isVkn ? taxInput : undefined;

      const result = isRestP2PEnabled
        ? await queryCustomerInfoViaRestP2P(tcknArg, vknArg)
        : await queryCustomerInfo(tcknArg, vknArg);

      if (result.success) {
        setQueryResult(result);
      } else {
        setError(result.errorMessage || 'Sorgu başarısız');
      }
    } catch (err: any) {
      setError(err.message || 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  }, [taxInput, isVkn, isTckn, isValidInput, isRestP2PEnabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidInput && !loading) {
      handleQuery();
    }
  }, [handleQuery, isValidInput, loading]);

  const getDisplayName = (): string => {
    if (!queryResult?.customerParty) return '';
    const cp = queryResult.customerParty;
    if (cp.CustomerType === 2) {
      return cp.CompanyName || '';
    }
    return `${cp.FirstName} ${cp.FamilyName}`.trim();
  };

  const handleUseForSale = useCallback(() => {
    if (!queryResult?.customerParty) return;
    onSelectForSale(queryResult.customerParty, isEInvoice, getDisplayName(), notificationPhone || undefined);
  }, [queryResult, onSelectForSale, isEInvoice, notificationPhone]);

  const handleSaveAsAccount = useCallback(async () => {
    if (!queryResult?.customerParty || !companyId) return;

    setSaving(true);
    setError(null);

    try {
      const cp = queryResult.customerParty;
      const displayName = getDisplayName();

      const payload = {
        taxNumber: cp.TaxNumber || '',
        companyName: displayName,
        taxOffice: cp.TaxOfficeName || cp.TaxOfficeCode || '',
        authorizedPerson: cp.CustomerType === 1 ? `${cp.FirstName} ${cp.FamilyName}`.trim() : displayName,
        phone: cp.Phone || '',
        email: cp.EMail || '',
        country: cp.Country || 'Türkiye',
        city: cp.City || '',
        district: cp.District || '',
        address: cp.Address || '',
        notes: isEInvoice ? 'E-Fatura Mükellefi' : 'E-Arşiv',
      };

      console.log('[CustomerQueryModal] Cari hesap bul/oluştur:', payload);

      const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_FIND_OR_CREATE_BY_TAX, payload);
      const result = response.data;
      const savedId = result?.data?.id || result?.id || '';

      console.log('[CustomerQueryModal] Cari hesap bul/oluştur sonuç:', { savedId, isNew: result?.isNew });
      setSaveSuccess(true);
      onSaveAndSelect(queryResult.customerParty, isEInvoice, String(savedId), displayName, notificationPhone || undefined);
    } catch (err: any) {
      console.error('[CustomerQueryModal] Cari kaydetme hatası:', err);
      const errorData = err.response?.data;
      if (errorData?.message) {
        setError(`Kayıt hatası: ${errorData.message}`);
      } else {
        setError(`Kayıt hatası: ${err.message || 'Bilinmeyen hata'}`);
      }
    } finally {
      setSaving(false);
    }
  }, [queryResult, companyId, onSaveAndSelect, isEInvoice]);

  const handleClose = useCallback(() => {
    setStep('select-type');
    setSelectedInvoiceType(null);
    setTaxInput('');
    setQueryResult(null);
    setError(null);
    setSaveSuccess(false);
    setNotificationPhone('');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step !== 'select-type' && (
            <IconButton onClick={handleBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <SearchIcon color="primary" />
          <Typography variant="h6">
            {step === 'select-type' ? t('customerQueryModal.selectTypeTitle') : t('customerQueryModal.queryTitle')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* ADIM 1: Fatura Türü Seçimi */}
        {step === 'select-type' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              {t('customerQueryModal.selectTypeDescription')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Paper
                elevation={2}
                onClick={() => handleSelectType('e-arsiv')}
                sx={{
                  p: 3,
                  flex: 1,
                  maxWidth: 220,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '2px solid transparent',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'info.main',
                    bgcolor: 'info.50',
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }}
              >
                <DescriptionIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {t('customerQueryModal.invoiceTypes.eArchive')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customerQueryModal.invoiceTypes.eArchiveDescription')}
                </Typography>
              </Paper>

              <Paper
                elevation={2}
                onClick={() => handleSelectType('e-fatura')}
                sx={{
                  p: 3,
                  flex: 1,
                  maxWidth: 220,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '2px solid transparent',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'success.main',
                    bgcolor: 'success.50',
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }}
              >
                <ReceiptLongIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {t('customerQueryModal.invoiceTypes.eInvoice')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customerQueryModal.invoiceTypes.eInvoiceDescription')}
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        {/* ADIM 2: TC/VKN Sorgu */}
        {step === 'query' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={isEInvoice ? t('customerQueryModal.invoiceTypes.eInvoice') : t('customerQueryModal.invoiceTypes.eArchive')}
                color={isEInvoice ? 'success' : 'info'}
                size="small"
                variant="filled"
              />
              <Typography variant="body2" color="text.secondary">
                {isEInvoice
                  ? t('customerQueryModal.queryDescription.eInvoice')
                  : t('customerQueryModal.queryDescription.eArchive')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label={t('customerQueryModal.taxInputLabel')}
                  value={taxInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 11) setTaxInput(val);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={t('customerQueryModal.taxInputPlaceholder')}
                  size="small"
                  inputProps={{ maxLength: 11, inputMode: 'numeric' }}
                  helperText={
                    taxInput.length > 0
                      ? taxInput.length === 10
                        ? t('customerQueryModal.helpers.vkn')
                        : taxInput.length === 11
                          ? t('customerQueryModal.helpers.tckn')
                          : t('customerQueryModal.helpers.lengthInfo', { count: taxInput.length })
                      : ' '
                  }
                  autoFocus
                />
                <Button
                  variant="contained"
                  onClick={handleQuery}
                  disabled={!isValidInput || loading}
                  sx={{ minWidth: 100, height: 40 }}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                >
                  {loading ? t('customerQueryModal.actions.querying') : t('customerQueryModal.actions.query')}
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('customerQueryModal.messages.savedAndSelected')}
              </Alert>
            )}

            {queryResult?.success && queryResult.customerParty && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: isEInvoice ? 'success.main' : 'info.main', borderWidth: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {queryResult.customerParty.CustomerType === 2 ? (
                    <BusinessIcon color="primary" />
                  ) : (
                    <PersonIcon color="primary" />
                  )}
                  <Typography variant="subtitle1" fontWeight="bold">
                    {getDisplayName()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={isEInvoice ? t('customerQueryModal.invoiceTypes.eInvoice') : t('customerQueryModal.invoiceTypes.eArchive')}
                    color={isEInvoice ? 'success' : 'info'}
                    size="small"
                    variant="filled"
                  />
                  {queryResult.rawInfo?.faalTerkDurumu && (
                    <Chip
                      label={queryResult.rawInfo.faalTerkDurumu === '1' ? t('customerQueryModal.status.active') : t('customerQueryModal.status.closed')}
                      color={queryResult.rawInfo.faalTerkDurumu === '1' ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {queryResult.customerParty.TaxOfficeName && (
                    <Typography variant="body2" color="text.secondary">
                      {t('customerQueryModal.taxOffice')}: {queryResult.customerParty.TaxOfficeName} ({queryResult.customerParty.TaxOfficeCode})
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {queryResult.customerParty.CustomerType === 2 ? 'VKN' : 'TCKN'}: {queryResult.customerParty.TaxNumber}
                  </Typography>
                  {queryResult.customerParty.Address && (
                    <Typography variant="body2" color="text.secondary">
                      {t('customerQueryModal.address')}: {queryResult.customerParty.Address}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                  {isEInvoice
                    ? t('customerQueryModal.saleInfo.eInvoice')
                    : t('customerQueryModal.saleInfo.eArchive')}
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    fullWidth
                    label={t('customerQueryModal.phoneLabel')}
                    placeholder={t('customerQueryModal.phonePlaceholder')}
                    value={notificationPhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d+\s]/g, '');
                      if (val.length <= 15) setNotificationPhone(val);
                    }}
                    size="small"
                    inputProps={{ maxLength: 15, inputMode: 'tel' }}
                    helperText={t('customerQueryModal.phoneHelper')}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                    }}
                  />
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose} color="inherit">
          {t('customerQueryModal.actions.close')}
        </Button>
        <Box sx={{ flex: 1 }} />
        {step === 'query' && queryResult?.success && queryResult.customerParty && !saveSuccess && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSaveAsAccount}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {saving ? t('customerQueryModal.actions.saving') : t('customerQueryModal.actions.saveAndSelect')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUseForSale}
              startIcon={<ShoppingCartIcon />}
            >
              {t('customerQueryModal.actions.useForSale')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerQueryModal;
